/**
 * Spotlight Carousel Block
 */
import Swiper from "swiper";
import { A11y, Autoplay, Keyboard } from "swiper/modules";

document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll(".spotlight-carousel").forEach((carousel) => {
    initializeSpotlightCarouselPatternReveal(carousel);
    initializeSpotlightCarousel(carousel);
  });
});

document.addEventListener("blockEditorInit", function (event) {
  const block = event.detail.block;

  if (!block) {
    return;
  }

  if (block.matches && block.matches(".spotlight-carousel")) {
    initializeSpotlightCarouselPatternReveal(block);
    initializeSpotlightCarousel(block);
  }

  block.querySelectorAll(".spotlight-carousel").forEach((carousel) => {
    initializeSpotlightCarouselPatternReveal(carousel);
    initializeSpotlightCarousel(carousel);
  });
});

function initializeSpotlightCarouselPatternReveal(carouselElement) {
  const patternElement = carouselElement.querySelector(".section-pattern__image");

  if (
    !patternElement ||
    carouselElement.dataset.spotlightCarouselPatternInitialized === "true"
  ) {
    return;
  }

  carouselElement.dataset.spotlightCarouselPatternInitialized = "true";

  if (!("IntersectionObserver" in window)) {
    carouselElement.classList.add("spotlight-carousel--pattern-visible");
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        carouselElement.classList.add("spotlight-carousel--pattern-visible");
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.25,
      rootMargin: "0px 0px -10% 0px",
    },
  );

  observer.observe(carouselElement);
}

function initializeSpotlightCarousel(carouselElement) {
  const railElement = carouselElement.querySelector(".spotlight-carousel__rail");
  const swiperContainer = carouselElement.querySelector(
    ".spotlight-carousel__swiper",
  );
  const prevButton = carouselElement.querySelector(
    ".spotlight-carousel__button--prev",
  );
  const nextButton = carouselElement.querySelector(
    ".spotlight-carousel__button--next",
  );
  const autoplayButton = carouselElement.querySelector(
    ".spotlight-carousel__autoplay-button",
  );
  const navButtons = Array.from(
    carouselElement.querySelectorAll("[data-spotlight-carousel-nav]"),
  );

  if (
    !swiperContainer ||
    swiperContainer.dataset.spotlightCarouselInitialized === "true"
  ) {
    return;
  }

  const updateRailWidth = () => {
    if (!railElement) {
      return;
    }

    const railBounds = railElement.getBoundingClientRect();
    const availableWidth = Math.max(0, window.innerWidth - railBounds.left);

    railElement.style.setProperty(
      "--spotlight-carousel-visible-width",
      `${availableWidth}px`,
    );
  };

  updateRailWidth();

  const slides = swiperContainer.querySelectorAll(".swiper-slide");

  if (slides.length < 2) {
    carouselElement.classList.add("spotlight-carousel--locked");
    window.addEventListener("resize", updateRailWidth);
    return;
  }

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const autoplayDelay = Math.max(
    2000,
    parseInt(carouselElement.dataset.spotlightCarouselAutoplayDelay || "7000", 10),
  );

  swiperContainer.dataset.spotlightCarouselInitialized = "true";

  const getActiveNavigationIndex = (swiperInstance) => {
    const lastIndex = navButtons.length - 1;

    if (lastIndex < 0) {
      return -1;
    }

    if (swiperInstance.isEnd) {
      return lastIndex;
    }

    if (
      Array.isArray(swiperInstance.visibleSlidesIndexes) &&
      swiperInstance.visibleSlidesIndexes.length > 0
    ) {
      return Math.max(
        0,
        Math.min(lastIndex, swiperInstance.visibleSlidesIndexes[0]),
      );
    }

    return Math.max(
      0,
      Math.min(lastIndex, swiperInstance.activeIndex ?? swiperInstance.realIndex ?? 0),
    );
  };

  const syncActiveNavigation = (swiperInstance) => {
    const activeIndex = getActiveNavigationIndex(swiperInstance);

    navButtons.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  };

  const syncAutoplayButton = (swiperInstance) => {
    if (!autoplayButton) {
      return;
    }

    const isPaused = !swiperInstance.autoplay?.running;

    autoplayButton.classList.toggle("is-paused", isPaused);
    autoplayButton.setAttribute("aria-pressed", isPaused ? "true" : "false");
    autoplayButton.setAttribute(
      "aria-label",
      isPaused ? "Start slide rotation" : "Pause slide rotation",
    );
  };

  const getPreviousNavigationIndex = (swiperInstance) => {
    const currentIndex = getActiveNavigationIndex(swiperInstance);
    const lastIndex = navButtons.length - 1;

    if (currentIndex <= 0) {
      return swiperInstance.params.rewind ? lastIndex : 0;
    }

    return currentIndex - 1;
  };

  const getNextNavigationIndex = (swiperInstance) => {
    const currentIndex = getActiveNavigationIndex(swiperInstance);
    const lastIndex = navButtons.length - 1;

    if (currentIndex >= lastIndex) {
      return swiperInstance.params.rewind ? 0 : lastIndex;
    }

    return currentIndex + 1;
  };

  const swiper = new Swiper(swiperContainer, {
    modules: [A11y, Autoplay, Keyboard],
    slidesPerView: 1.05,
    slidesOffsetBefore: 0,
    spaceBetween: 40,
    slidesOffsetAfter: 40,
    speed: prefersReducedMotion ? 0 : 700,
    autoHeight: true,
    loop: false,
    rewind: slides.length > 1,
    watchOverflow: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    a11y: {
      enabled: true,
      prevSlideMessage: "Previous slide",
      nextSlideMessage: "Next slide",
    },
    autoplay: {
      delay: autoplayDelay,
      disableOnInteraction: false,
      pauseOnMouseEnter: false,
    },
    breakpoints: {
      768: {
        slidesPerView: 1.35,
        slidesOffsetBefore: 0,
        spaceBetween: 40,
        slidesOffsetAfter: 40,
      },
      992: {
        slidesPerView: 1.75,
        slidesOffsetBefore: 0,
        spaceBetween: 112,
        slidesOffsetAfter: 112,
      },
    },
    on: {
      init() {
        if (prefersReducedMotion) {
          this.autoplay.stop();
        }

        syncActiveNavigation(this);
        syncAutoplayButton(this);
      },
      slideChange() {
        syncActiveNavigation(this);
      },
      transitionEnd() {
        syncActiveNavigation(this);
      },
      autoplayStart() {
        syncAutoplayButton(this);
      },
      autoplayStop() {
        syncAutoplayButton(this);
      },
      resize() {
        syncActiveNavigation(this);
        syncAutoplayButton(this);
      },
    },
  });

  navButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const targetIndex = parseInt(
        button.dataset.spotlightCarouselIndex || "-1",
        10,
      );

      if (Number.isNaN(targetIndex) || targetIndex < 0) {
        return;
      }

      swiper.slideTo(targetIndex);
    });
  });

  if (prevButton) {
    prevButton.addEventListener("click", () => {
      swiper.slideTo(getPreviousNavigationIndex(swiper));
    });
  }

  if (nextButton) {
    nextButton.addEventListener("click", () => {
      swiper.slideTo(getNextNavigationIndex(swiper));
    });
  }

  if (autoplayButton) {
    autoplayButton.addEventListener("click", () => {
      if (!swiper.autoplay) {
        return;
      }

      if (swiper.autoplay.running) {
        swiper.autoplay.stop();
      } else {
        swiper.autoplay.start();
      }

      syncAutoplayButton(swiper);
    });
  }

  requestAnimationFrame(() => {
    updateRailWidth();
    swiper.update();
    swiper.updateAutoHeight(0);
    syncActiveNavigation(swiper);
    syncAutoplayButton(swiper);
  });

  window.addEventListener("resize", () => {
    updateRailWidth();
    swiper.update();
    swiper.updateAutoHeight(0);
    syncActiveNavigation(swiper);
    syncAutoplayButton(swiper);
  });
}
